import { Injectable } from "@angular/core";
import { RouteService, SearchParamValue } from "@common/route/route.service";

export interface IUrlParams {
    name: string;
    value: string | undefined;
}

@Injectable({
    providedIn: "root",
})
export class UrlFilterService {
    constructor(private routeService: RouteService) {
    }

    public async setFilter(filters: Record<string, SearchParamValue>) {
        for (const [key, value] of Object.entries(filters)) {
            //we only want to clean delete these value from the url. !value doesn't work since we don't want get rid of numeric zero and boolean false.
            if (value === undefined || value === null || value === "") {
                await this.routeService.deleteSearchParameter(key);
            } else {
                await this.routeService.updateSearchParameterValue(key, value);
            }
        }
    }

    public getFilters(params: string[]): IUrlParams[] {
        return this.getSearchParametersValue(params);
    }

    public getFiltersFromUrl<T>(params: string[], defaultFilters: T): T {
        const urlParams = this.getFilters(params);
        const currentFilter: Partial<T> = {};
        for (const urlParam of urlParams) {
            if (urlParam.value !== undefined) {
                currentFilter[urlParam.name as keyof T] = this.deserialise(urlParam.value) as any;
            } else {
                // not on search param -> default
                currentFilter[urlParam.name as keyof T] = defaultFilters[urlParam.name as keyof T] as any;
            }

            if (currentFilter[urlParam.name as keyof T] === undefined) {
                // remove undefined properties
                delete currentFilter[urlParam.name as keyof T];
            }
        }

        return currentFilter as T;
    }

    public setFilterToUrl<T>(
        params: string[],
        filters: T,
        defaultFilters: T,
        stringifiedValueTransformer: (key: keyof T, stringifiedValue?: string) => string | undefined
            = (_key, stringifiedValue) => stringifiedValue,
    ) {
        const filterObject: { [key: string]: string | undefined } = {};
        for (const key of params) {
            const defaultValue = this.stringify(defaultFilters[key as keyof T] as any);
            const currentValue = stringifiedValueTransformer(key as keyof T, this.stringify(filters[key as keyof T] as any));
            const urlValue = defaultValue !== currentValue ? currentValue : undefined;
            filterObject[key] = urlValue;
        }

        return this.setFilter(filterObject);
    }

    public async clearFilters(params: string[]) {
        for (const param of params) {
            await this.routeService.deleteSearchParameter(param);
        }
    }

    public getSearchParametersValue(paramNames: string[]) {
        return paramNames.map((name) => ({ name, value: this.routeService.getSearchParameterValue(name) }));
    }

    public urlHasSearchParams() {
        return this.routeService.getSearchParameters !== undefined;
    }

    private deserialise(value?: string): number[] | boolean | Date | undefined {
        if (value === undefined || value === null || value === "undefined") {
            return undefined;
        }

        // Try to parse as array (comma-separated or single number)
        if (value.includes(",")) {
            return value.split(",").map(Number);
        }

        // Check if it's a single number (for arrays with one element)
        const singleNumber = Number(value);
        if (!isNaN(singleNumber)) {
            return [singleNumber];
        }

        // Try to parse as boolean
        if (["true", "false"].includes(value)) {
            return value === "true";
        }

        // Try to parse as date
        const date = new Date(value);
        return isNaN(date.getTime()) ? undefined : date;
    }

    private stringify(value: number[] | boolean | Date | undefined | null) {
        if (value === undefined || value === null) {
            return undefined;
        }
        if (Array.isArray(value)) {
            return value.join(",");
        }
        if (typeof value === "boolean") {
            return value ? "true" : "false";
        }
        if (value instanceof Date) {
            return value.toISOString();
        }
        return String(value);
    }
}


